import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/apiClient";
import { EQUIPMENT_TYPES } from "@/lib/status";
import { toast } from "sonner";
import { Wind, Save, User, ShieldCheck } from "lucide-react";
import EquipmentMediaFields from "@/components/app/EquipmentMediaFields";

const EMPTY = {
  equipment_type: "", brand: "", model: "", serial_number: "", location: "", capacity_btu: "", voltage: "", install_date: "",
  customer_name: "", customer_phone: "", customer_cep: "", customer_address: "", qr_code: "", photo: "",
  warranty_type: "", warranty_end: "", warranty_coverage: "",
};

export default function StaffEquipmentDialog({ open, onOpenChange, presetCustomer, requestId, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY,
        customer_name: presetCustomer?.name || "",
        customer_phone: presetCustomer?.phone || "",
        customer_cep: presetCustomer?.cep || "",
        customer_address: presetCustomer?.address || "",
      });
    }
  }, [open, presetCustomer]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.equipment_type) { toast.error("Selecione o tipo do equipamento."); return; }
    if (!form.serial_number.trim() && !form.brand.trim()) {
      toast.error("Informe ao menos a marca ou o nº de série."); return;
    }
    if (!form.photo) { toast.error("Envie a foto do equipamento."); return; }
    setBusy(true);
    try {
      const payload = {
        equipment_type: form.equipment_type,
        brand: form.brand || null,
        model: form.model || null,
        serial_number: form.serial_number || null,
        location: form.location || null,
        capacity_btu: form.capacity_btu || null,
        voltage: form.voltage || null,
        install_date: form.install_date || null,
        customer_name: form.customer_name || null,
        customer_phone: form.customer_phone || null,
        customer_cep: form.customer_cep || null,
        customer_address: form.customer_address || null,
        request_id: requestId || null,
        ...(form.qr_code ? { qr_code: form.qr_code.trim() } : {}),
        ...(form.photo ? { photo: form.photo } : {}),
        warranty: form.warranty_type
          ? { type: form.warranty_type, end: form.warranty_end || null, coverage: form.warranty_coverage || null }
          : null,
      };
      const { data } = await api.post("/staff/equipment", payload);
      toast.success("Equipamento cadastrado! QR Code gerado.");
      onOpenChange(false);
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="staff-equipment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wind className="h-5 w-5 text-[#1e7be0]" /> Cadastrar equipamento</DialogTitle>
          <DialogDescription>Gera um QR Code com os dados e o histórico de serviços do aparelho.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Tipo *</Label>
            <Select value={form.equipment_type} onValueChange={(v) => setForm((f) => ({ ...f, equipment_type: v }))}>
              <SelectTrigger data-testid="staff-eq-type-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {EQUIPMENT_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`staff-eq-type-${t}`}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Marca</Label>
              <Input value={form.brand} onChange={set("brand")} placeholder="Ex: Elgin" data-testid="staff-eq-brand-input" /></div>
            <div className="space-y-1.5"><Label>Modelo</Label>
              <Input value={form.model} onChange={set("model")} placeholder="Ex: Eco 12000" data-testid="staff-eq-model-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Nº de série</Label>
              <Input value={form.serial_number} onChange={set("serial_number")} placeholder="Número de série" data-testid="staff-eq-serial-input" /></div>
            <div className="space-y-1.5"><Label>Capacidade (BTU)</Label>
              <Input value={form.capacity_btu} onChange={set("capacity_btu")} placeholder="Ex: 12000" data-testid="staff-eq-btu-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Tensão</Label>
              <Input value={form.voltage} onChange={set("voltage")} placeholder="Ex: 220V" data-testid="staff-eq-voltage-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Local (opcional)</Label>
              <Input value={form.location} onChange={set("location")} placeholder="Ex: Sala, quarto..." data-testid="staff-eq-location-input" /></div>
            <div className="space-y-1.5"><Label>Data de instalação</Label>
              <Input type="date" value={form.install_date} onChange={set("install_date")} data-testid="staff-eq-install-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Nº do QR / Patrimônio (opcional)</Label>
            <Input value={form.qr_code} onChange={set("qr_code")} placeholder="Número impresso na etiqueta (ex: 10001)" data-testid="staff-eq-qrcode-input" /></div>
          <EquipmentMediaFields qrCode={form.qr_code} onQr={(v) => setForm((f) => ({ ...f, qr_code: v }))}
            photo={form.photo} onPhoto={(p) => setForm((f) => ({ ...f, photo: p }))} />

          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <Label className="text-sm flex items-center gap-1.5"><User className="h-4 w-4 text-[#1e7be0]" /> Dados do cliente</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label className="text-xs">Nome</Label>
                <Input value={form.customer_name} onChange={set("customer_name")} placeholder="Nome do cliente" data-testid="staff-eq-customer-name-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Telefone</Label>
                <Input value={form.customer_phone} onChange={set("customer_phone")} placeholder="(21) 99999-9999" data-testid="staff-eq-customer-phone-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label className="text-xs">CEP</Label>
                <Input value={form.customer_cep} onChange={set("customer_cep")} placeholder="00000-000" data-testid="staff-eq-customer-cep-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Endereço</Label>
                <Input value={form.customer_address} onChange={set("customer_address")} placeholder="Endereço" data-testid="staff-eq-customer-address-input" /></div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <Label className="text-sm flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[#1e7be0]" /> Garantia (opcional)</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label className="text-xs">Tipo</Label>
                <Select value={form.warranty_type || "none"} onValueChange={(v) => setForm((f) => ({ ...f, warranty_type: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="staff-eq-warranty-type"><SelectValue placeholder="Sem garantia" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem garantia</SelectItem>
                    <SelectItem value="fabrica" data-testid="staff-eq-warranty-fabrica">Fábrica</SelectItem>
                    <SelectItem value="servico" data-testid="staff-eq-warranty-servico">Serviço</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Validade</Label>
                <Input type="date" value={form.warranty_end} onChange={set("warranty_end")} disabled={!form.warranty_type} data-testid="staff-eq-warranty-end" /></div>
            </div>
            {form.warranty_type && (
              <div className="space-y-1.5"><Label className="text-xs">Cobertura</Label>
                <Input value={form.warranty_coverage} onChange={set("warranty_coverage")} placeholder="Ex: Compressor, mão de obra" data-testid="staff-eq-warranty-coverage" /></div>
            )}
          </div>

          <DialogFooter>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5" disabled={busy} data-testid="staff-eq-save-button">
              <Save className="h-4 w-4" /> {busy ? "Salvando..." : "Salvar e gerar QR Code"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
